
/**
 * Role verification utility to help debug permission issues
 */
import { supabase } from "@/integrations/supabase/client";

/**
 * Verifies a user's roles directly with Supabase
 * @returns Object containing role verification results
 */
export const verifyUserRoles = async () => {
  try {
    console.log("Verifying user roles directly with Supabase...");
    
    // Get current session
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      console.error("Error getting session:", sessionError);
      return {
        success: false,
        error: sessionError.message,
        session: null,
        roles: null,
        hasAdmin: false,
        hasDeveloper: false,
        hasTester: false
      };
    }
    
    if (!sessionData.session) {
      console.warn("No active session found");
      return {
        success: false,
        error: "No active session",
        session: null,
        roles: null,
        hasAdmin: false,
        hasDeveloper: false,
        hasTester: false
      };
    }
    
    const userId = sessionData.session.user.id;
    console.log("Current user ID:", userId);
    
    // Get user roles
    const { data: rolesData, error: rolesError } = await supabase
      .rpc('get_user_roles', { user_id: userId });
      
    if (rolesError) {
      console.error("Error fetching user roles:", rolesError);
      return {
        success: false,
        error: rolesError.message,
        session: sessionData.session,
        roles: null,
        hasAdmin: false,
        hasDeveloper: false,
        hasTester: false
      };
    }
    
    // Ensure roles are strings and uppercase
    const roles = (rolesData as string[]).map(role => 
      typeof role === 'string' ? role.toUpperCase() : String(role).toUpperCase()
    );
    
    console.log("User roles:", roles);
    
    // Check for specific roles
    const hasAdmin = roles.includes("ADMIN");
    const hasDeveloper = roles.includes("DEVELOPER");
    const hasTester = roles.includes("TESTER");
    
    return {
      success: true,
      error: null,
      session: sessionData.session,
      roles,
      hasAdmin,
      hasDeveloper,
      hasTester
    };
  } catch (err) {
    console.error("Unexpected error verifying roles:", err);
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
      session: null,
      roles: null,
      hasAdmin: false,
      hasDeveloper: false,
      hasTester: false
    };
  }
};
